import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { AUTH_API_BASE_URL } from "../config/api-config";
import {
  getBrowserBinding, rememberPending, forgetPending, currentReturnPath, claimResume, authConfig,
} from "../utils/bankIdSession";

const BASE = `${AUTH_API_BASE_URL}/BankId`;

export const MODE_SAME_DEVICE = "sameDevice";
export const MODE_OTHER_DEVICE = "otherDevice";

// Hints produced here (in addition to BankID's own hintCodes).
export const HINT_STORAGE_UNAVAILABLE = "storageUnavailable";
export const HINT_INCOMPATIBLE_SERVER = "incompatibleServer";
export const HINT_AWAITING_RETURN = "awaitingReturn";

const unwrap = (res) => res?.data?.result ?? res?.data;
// 403/404 answers carry { status: "failed", hintCode } (bindingMismatch, unknownOrder, …).
const hintOf = (err) => err?.response?.data?.hintCode ?? err?.response?.data?.error ?? null;

/**
 * Hook for the native Swedish BankID RP API (REACT_APP_AUTH_PROVIDER=bankid).
 *
 *   const bankId = useBankId();
 *   bankId.start({ mode: "sameDevice" | "otherDevice", scope, onComplete, onFailed, onRegistrationRequired });
 *   bankId.resume(orderRef, { ...same callbacks });   // after a reload / return; never launches the app
 *
 * purpose "link" (signed-in user verifying their identity): the order is collected through
 * /BankId/Link, which puts the identity on the current account instead of logging in, so
 * onComplete gets no token. Use a scope of its own so no login dialog ever resumes it.
 *
 * Every call carries this browser's binding key; the server only answers the browser that
 * started the order. A same-device order answers "pending / awaitingReturn" after the user
 * finished in the app until the server has proven this browser is where BankID ran.
 */
export function useBankId() {
  const [status, setStatus] = useState(null);       // null | "pending" | "failed" | "complete" | "registration_required"
  const [hintCode, setHintCode] = useState(null);
  const [completionData, setCompletionData] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [expireInSeconds, setExpireInSeconds] = useState(null);
  const [error, setError] = useState(null);
  const [orderRef, setOrderRef] = useState(null);
  const [autoStartToken, setAutoStartToken] = useState(null);
  const [qrStartToken, setQrStartToken] = useState(null);
  const [qrStartSecret, setQrStartSecret] = useState(null);
  const [mode, setMode] = useState(null);

  const pollTimer = useRef(null);
  const runRef = useRef(0);          // bumps on every start/resume/cancel; stale loops stop
  const activeRef = useRef(null);    // orderRef still worth cancelling (null once finished)
  const startingRef = useRef(false); // an /Auth call is in flight

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  // Unmounting (dialog closed, page left) stops this component's polling and its "page visible"
  // listeners. The BankID order itself is not cancelled: a pending order can still be resumed.
  const listenersRef = useRef(null);
  useEffect(() => () => {
    runRef.current += 1;
    stopPolling();
    listenersRef.current?.();
  }, [stopPolling]);

  const resetOrder = () => {
    setOrderRef(null);
    setAutoStartToken(null);
    setQrStartToken(null);
    setQrStartSecret(null);
    setMode(null);
  };

  const finish = (ref) => {
    if (activeRef.current === ref) activeRef.current = null;
    forgetPending(ref);
  };

  // One poll loop for start, resume and registration.
  const poll = useCallback((ref, run, opts, profile = {}) => {
    // A BankID order lives ~3 minutes. On a phone, switching to the BankID app makes the browser
    // abort the request in flight — that is not a failure: keep polling through transient errors
    // (network, 5xx, 429) until the order expires, and poll at once when the page is visible again.
    const deadline = Date.now() + 3.5 * 60 * 1000;
    let transientFailures = 0;
    const onVisible = () => {
      if (document.visibilityState !== "visible" || runRef.current !== run) return;
      clearTimeout(pollTimer.current);
      tick();
    };
    const stopListening = () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    listenersRef.current?.();          // only one live poll loop's listeners at a time
    listenersRef.current = stopListening;
    const tick = async () => {
      if (runRef.current !== run) { stopListening(); return; }
      try {
        const collected = unwrap(opts.purpose === "link"
          ? await axios.post(`${BASE}/Link`, { orderRef: ref, browserBinding: getBrowserBinding() }, authConfig())
          : await axios.post(`${BASE}/Collect`, {
            orderRef: ref,
            browserBinding: getBrowserBinding(),
            email: profile.email ?? null,
            password: profile.password ?? null,
            personalContact: profile.personalContact ?? null,
            isBuyer: profile.isBuyer ?? opts.isBuyer ?? false,
          }));
        if (runRef.current !== run) { stopListening(); return; }
        transientFailures = 0;

        setStatus(collected.status);
        setHintCode(collected.hintCode ?? null);

        if (collected.status === "registration_required") {
          stopListening();
          setCompletionData(collected.completionData);
          opts.onRegistrationRequired?.(collected.completionData);
          return;
        }
        if (collected.status === "complete") {
          stopListening();
          finish(ref);
          setCompletionData(collected.completionData);
          setAccessToken(collected.accessToken);
          setExpireInSeconds(collected.expireInSeconds);
          opts.onComplete?.(collected.accessToken, collected.expireInSeconds, collected.completionData,
            { isNewUser: collected.isNewUser === true });
          return;
        }
        if (collected.status === "failed") {
          stopListening();
          finish(ref);
          opts.onFailed?.(collected.hintCode);
          return;
        }
        // pending (incl. awaitingReturn) or unknown: keep polling every 2 s, as BankID specifies.
        pollTimer.current = setTimeout(tick, 2000);
      } catch (err) {
        if (runRef.current !== run) { stopListening(); return; }
        const httpStatus = err.response?.status;
        const transient = !err.response || httpStatus >= 500 || httpStatus === 429;
        if (transient && Date.now() < deadline) {
          // keep the order: the user is probably in the BankID app right now
          transientFailures += 1;
          pollTimer.current = setTimeout(tick, Math.min(8000, 2000 * 2 ** Math.min(transientFailures - 1, 2)));
          return;
        }
        stopListening();
        finish(ref);
        const hint = hintOf(err) ?? "expiredTransaction";
        setError(err.response?.data?.message ?? err.message ?? "BankID collect error");
        setStatus("failed");
        setHintCode(hint);
        opts.onFailed?.(hint);
      }
    };
    tick();
  }, []);

  const cancel = useCallback(async (ref) => {
    runRef.current += 1;
    stopPolling();
    startingRef.current = false;
    const activeOrder = ref ?? activeRef.current;
    activeRef.current = null;
    resetOrder();
    setStatus(null);
    if (activeOrder) {
      forgetPending(activeOrder);
      try {
        await axios.post(`${BASE}/Cancel`, { orderRef: activeOrder, browserBinding: getBrowserBinding() });
      } catch (_) { /* best-effort */ }
    }
  }, [stopPolling]);

  /**
   * Start a BankID authentication order.
   * @param {object} opts
   * @param {"sameDevice"|"otherDevice"} opts.mode
   * @param {string}   [opts.scope]                  - Which dialog owns the order (resume after reload)
   * @param {"auth"|"link"} [opts.purpose]          - "link": verify the signed-in user (no login)
   * @param {string}   [opts.userVisibleData]
   * @param {boolean}  [opts.isBuyer]                - Role for auto-registration
   * @param {function} [opts.onRegistrationRequired]
   * @param {function} [opts.onComplete]             - (accessToken, expireInSeconds, completionData, { isNewUser })
   * @param {function} [opts.onFailed]               - Called with hintCode on failure
   * @param {function} [opts.onStarted]              - Called with the order once it exists
   */
  const start = useCallback(async (opts = {}) => {
    if (startingRef.current) return;          // a double click must not create a second order
    runRef.current += 1;
    const run = runRef.current;
    stopPolling();
    setError(null);
    setStatus(null);
    setHintCode(null);
    setCompletionData(null);
    setAccessToken(null);
    // Drop the previous order's tokens so nothing can launch the BankID app with a stale one.
    resetOrder();

    const fail = (hint, message) => {
      setError(message ?? hint);
      setStatus("failed");
      setHintCode(hint);
      opts.onFailed?.(hint);
    };

    const browserBinding = getBrowserBinding();
    if (!browserBinding) {
      fail(HINT_STORAGE_UNAVAILABLE);
      return;
    }

    const requestedMode = opts.mode === MODE_SAME_DEVICE ? MODE_SAME_DEVICE : MODE_OTHER_DEVICE;
    startingRef.current = true;
    let authData;
    try {
      authData = unwrap(await axios.post(`${BASE}/Auth`, {
        mode: requestedMode,
        browserBinding,
        returnPath: requestedMode === MODE_SAME_DEVICE ? currentReturnPath() : null,
        personalNumber: opts.personalNumber ?? null,
        // Text shown in the user's BankID app during login (e.g. "Log in to Klargo").
        userVisibleData: opts.userVisibleData ?? null,
      }));
    } catch (err) {
      startingRef.current = false;
      if (runRef.current !== run) return;
      fail(hintOf(err) ?? "startFailed", err.response?.data?.error?.message ?? err.response?.data?.message ?? err.message);
      return;
    }
    startingRef.current = false;

    if (runRef.current !== run) {
      // Cancelled (or restarted) while /Auth was in flight: this order is nobody's anymore.
      axios.post(`${BASE}/Cancel`, { orderRef: authData?.orderRef, browserBinding }).catch(() => {});
      return;
    }

    // A server without browser binding would hand out an unprotected order: refuse it rather
    // than silently falling back to the old, deprecated return handling.
    if (authData?.binding !== "browser" || authData?.mode !== requestedMode) {
      axios.post(`${BASE}/Cancel`, { orderRef: authData?.orderRef }).catch(() => {});
      fail(HINT_INCOMPATIBLE_SERVER);
      return;
    }

    activeRef.current = authData.orderRef;
    if (requestedMode === MODE_SAME_DEVICE) {
      // Lets a reload or a new tab (the BankID app's return) resume this order.
      rememberPending({ orderRef: authData.orderRef, purpose: "auth", scope: opts.scope ?? "default" });
      // This page polls it already: a same-tab return must not resume it a second time.
      claimResume(authData.orderRef);
    }
    setOrderRef(authData.orderRef);
    setMode(requestedMode);
    setAutoStartToken(authData.autoStartToken ?? null);
    setQrStartToken(authData.qrStartToken ?? null);
    setQrStartSecret(authData.qrStartSecret ?? null);
    setStatus("pending");
    opts.onStarted?.(authData);

    poll(authData.orderRef, run, opts);
  }, [poll, stopPolling]);

  /** After registration_required: collect again with the profile details. */
  const submitRegistration = useCallback(async (ref, email, password, personalContact, isBuyer, _completionData, opts = {}) => {
    runRef.current += 1;
    const run = runRef.current;
    setError(null);
    setStatus("pending");
    try {
      const collected = unwrap(await axios.post(`${BASE}/Collect`, {
        orderRef: ref,
        browserBinding: getBrowserBinding(),
        email,
        password,
        personalContact,
        isBuyer: isBuyer ?? false,
      }));
      if (runRef.current !== run) return;
      setStatus(collected.status);

      if (collected.status === "complete") {
        finish(ref);
        setAccessToken(collected.accessToken);
        setExpireInSeconds(collected.expireInSeconds);
        opts.onComplete?.(collected.accessToken, collected.expireInSeconds, collected.completionData);
      } else if (collected.status === "registration_required") {
        setError("Registration details are still required. Please review the form and try again.");
      } else if (collected.status === "failed") {
        finish(ref);
        setError("BankID session expired. Please try again.");
        opts.onFailed?.(collected.hintCode);
      }
    } catch (err) {
      setError(err.response?.data?.message ?? err.message ?? "Registration error");
      setStatus("registration_required");
    }
  }, []);

  /**
   * Resume an existing order (reload, or the BankID app's return in a new tab). Never launches
   * the BankID app and never starts a new order.
   */
  const resume = useCallback((savedOrderRef, opts = {}) => {
    runRef.current += 1;
    const run = runRef.current;
    stopPolling();
    resetOrder();
    setError(null);
    setHintCode(null);
    setStatus("pending");
    setOrderRef(savedOrderRef);
    setMode(MODE_SAME_DEVICE);
    activeRef.current = savedOrderRef;
    poll(savedOrderRef, run, opts);
  }, [poll, stopPolling]);

  return {
    start,
    cancel,
    resume,
    resumePoll: resume,
    submitRegistration,
    status,
    hintCode,
    completionData,
    accessToken,
    expireInSeconds,
    orderRef,
    mode,
    autoStartToken,
    qrStartToken,
    qrStartSecret,
    error,
  };
}
